import React, { useMemo, useState } from "react";

// Figma node 540:6672. Choices stay a draft until Apply Filters; Cancel drops them.
const ICONS = "/assets/mobile/filter";
const SORT_GRADIENT  = "linear-gradient(45deg, #9E85DD 25%, #7B39EC 59.86%)";
const APPLY_GRADIENT = "linear-gradient(11.75deg, #9E85DD 25%, #7B39EC 59.86%)";
const BRANDS_SHOWN   = 4;

const BODY_TYPE_IMG = {
  Hatchback: `${ICONS}/hatchback.png`,
  Sedan: `${ICONS}/sedan.png`,
  SUV: `${ICONS}/suv.png`,
  "Crossover SUV": `${ICONS}/crossover-suv.png`,
  "Compact Sedan": `${ICONS}/compact-sedan.png`,
  MUV: `${ICONS}/muv.png`,
  MPV: `${ICONS}/muv.png`,
};

const Divider = () => <div className="w-full h-px bg-[#e5e7eb] shrink-0" />;

const SectionTitle = ({ children }) => (
  <span className="text-[#002b5b] text-[14px] font-bold">{children}</span>
);

const OptionRow = ({ title, options, selected, onToggle }) => (
  <div className="flex flex-col gap-3 p-4">
    <SectionTitle>{title}</SectionTitle>
    <div className="flex gap-2">
      {options.map(option => {
        const isSelected = selected?.includes(option);
        return (
          <button
            key={option}
            type="button"
            onClick={() => onToggle(option)}
            className={`flex-1 min-w-0 h-8 px-1 rounded-[8px] border text-[14px] font-medium transition-colors ${isSelected ? "bg-[#6262f2] border-[#6262f2] text-white" : "bg-white border-[#dadada] text-[#2d2d2d]"}`}
          >
            {option}
          </button>
        );
      })}
    </div>
  </div>
);

const YearSelect = ({ label, value, years, onChange }) => (
  <label className="flex-1 min-w-0 flex flex-col gap-1.5">
    <span className="text-[#777] text-[10px] font-medium">{label}</span>
    <span className="relative block">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full h-10 bg-white border border-[#e6e6e6] rounded-[6px] pl-3 pr-8 appearance-none outline-none text-[#212121] text-[12px] font-medium cursor-pointer"
      >
        <option value="">Any</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <img src={`${ICONS}/chevron-down.svg`} alt="" width="16" height="16" className="absolute right-2.5 top-3 pointer-events-none" />
    </span>
  </label>
);

const FilterSheetMob = ({
  originalData,
  bodyTypes,
  isEvMode,
  selectedFilters,
  sortOption,
  yearFrom,
  yearTo,
  countCars,
  onApply,
  onCancel,
}) => {
  // The drawer destroys this on close, so each open starts from the applied values
  const [filters, setFilters] = useState(selectedFilters);
  const [sort,    setSort]    = useState(sortOption);
  const [from,    setFrom]    = useState(yearFrom);
  const [to,      setTo]      = useState(yearTo);
  const [brandOpen,  setBrandOpen]  = useState(true);
  const [allBrands,  setAllBrands]  = useState(false);

  const years = useMemo(
    () => [...new Set((originalData || []).map(c => c.modelYear).filter(Boolean))].sort(),
    [originalData]
  );
  const brands = useMemo(
    () => [...new Set((originalData || []).map(c => c.brand).filter(Boolean))].sort(),
    [originalData]
  );

  const toggle = (title, value) => {
    setFilters(prev => {
      const current = prev[title] || [];
      const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      const next = { ...prev, [title]: updated };
      if (updated.length === 0) delete next[title];
      return next;
    });
  };

  const resetAll = () => {
    setFilters({});
    setSort("lowtohigh");
    setFrom("");
    setTo("");
  };

  const count = countCars(filters, from, to);

  return (
    <div className="w-full h-full bg-white flex flex-col font-['Outfit']">
      <div className="p-6 shrink-0 flex items-center justify-between gap-2">
        <span className="min-w-0 text-[#002b5b] text-[clamp(20px,5.9vw,24px)] font-bold leading-[32px] whitespace-nowrap">Sort &amp; Filter</span>
        <button type="button" aria-label="Close" onClick={onCancel} className="size-8 shrink-0 flex items-center justify-center">
          <img src={`${ICONS}/x.svg`} alt="" width="24" height="24" />
        </button>
      </div>
      <Divider />

      {/* Sort — fixed above the scrolling sections */}
      <div className="p-4 shrink-0 flex items-center gap-2">
        <span className="text-[14px] font-medium text-black whitespace-nowrap">Sort by :</span>
        <div className="flex-1 min-w-0 bg-white border border-[#e5e7eb] rounded-[8px] flex items-center justify-between gap-1 pl-[clamp(6px,2vw,12px)] pr-1.5 py-1.5">
          <span className="text-[14px] font-medium text-black whitespace-nowrap">{sort === "lowtohigh" ? "Low to high" : "High to low"}</span>
          <button
            type="button"
            aria-label="Change sort order"
            onClick={() => setSort(s => s === "lowtohigh" ? "hightolow" : "lowtohigh")}
            className="relative size-8 rounded-full flex items-center justify-center shrink-0 shadow-[1px_-1px_2px_0px_rgba(43,115,255,0.3),-1px_1px_2px_0px_rgba(31,83,197,0.5)]"
            style={{ background: SORT_GRADIENT }}
          >
            <img src={`${ICONS}/sort-${sort === "lowtohigh" ? "low-to-high" : "high-to-low"}.svg`} alt="" className="h-[15px] w-auto" />
            <span className="absolute inset-0 rounded-full pointer-events-none shadow-[inset_-2px_-2px_4px_0px_rgba(31,83,197,0.2),inset_2px_2px_4px_0px_rgba(31,83,197,0.2),inset_-2px_2px_4px_0px_rgba(43,115,255,0.9),inset_2px_-2px_5px_0px_rgba(31,83,197,0.9)]" />
          </button>
        </div>
      </div>
      <Divider />

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain flex flex-col">
        {/* Fuel & Transmission — hidden in EV mode */}
        {!isEvMode && <>
          <OptionRow
            title="Transmission"
            options={["Manual", "Automatic"]}
            selected={filters["Transmission Type"]}
            onToggle={v => toggle("Transmission Type", v)}
          />
          <Divider />
          <OptionRow
            title="Fuel Type"
            options={["Petrol", "Diesel"]}
            selected={filters["Fuel Type"]}
            onToggle={v => toggle("Fuel Type", v)}
          />
          <Divider />
        </>}

        <div className="flex flex-col gap-3 p-4">
          <SectionTitle>Car Type</SectionTitle>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-x-3 gap-y-3.5">
            {bodyTypes.map(({ label, key }) => {
              const isSelected = filters["Vehicle Body Type"]?.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggle("Vehicle Body Type", key)}
                  className={`min-w-0 h-[92px] bg-white border rounded-[12px] px-1 py-2.5 flex flex-col items-center justify-center gap-2 ${isSelected ? "border-[#e8273c]" : "border-[#e0e0e0]"}`}
                >
                  <span className="relative block w-full flex-1 min-h-0">
                    <img
                      src={BODY_TYPE_IMG[label] || "/assets/sample.png"}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={e => { e.target.style.display = "none"; }}
                    />
                    {isSelected && (
                      <span className="absolute right-px top-0 size-3 bg-[#e8273c] rounded-full flex items-center justify-center">
                        <img src={`${ICONS}/check.svg`} alt="" width="8" height="8" />
                      </span>
                    )}
                  </span>
                  <span className="w-full text-center text-[#1a1a1a] text-[clamp(10px,2.8vw,11px)] font-semibold leading-none whitespace-nowrap shrink-0">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <Divider />

        {years.length > 0 && <>
          <div className="flex flex-col gap-4 p-4">
            <span className="text-[#1a1917] text-[16px] font-semibold leading-6 tracking-[0.6px]">Model Year</span>
            <div className="flex gap-3">
              <YearSelect label="From" value={from} years={years} onChange={setFrom} />
              <YearSelect label="To"   value={to}   years={years} onChange={setTo} />
            </div>
          </div>
          <Divider />
        </>}

        {brands.length > 0 && (
          <div className="flex flex-col gap-4 p-4">
            <button type="button" onClick={() => setBrandOpen(o => !o)} className="flex items-center justify-between w-full">
              <span className="text-[#1a1917] text-[16px] font-semibold leading-6 tracking-[0.6px]">Brand</span>
              <img src={`${ICONS}/chevron-up.svg`} alt="" width="24" height="24" className={`transition-transform ${brandOpen ? "rotate-180" : ""}`} />
            </button>
            {brandOpen && (
              <div className="flex flex-col gap-1">
                <div className="flex flex-col gap-2.5">
                  {(allBrands ? brands : brands.slice(0, BRANDS_SHOWN)).map(brand => {
                    const isSelected = filters["Brand"]?.includes(brand);
                    return (
                      <button key={brand} type="button" onClick={() => toggle("Brand", brand)} className="flex items-center gap-3 w-full text-left">
                        <span className={`size-[18px] rounded-[4px] border-[1.5px] flex items-center justify-center shrink-0 ${isSelected ? "bg-[#6262f2] border-[#6262f2]" : "bg-white border-[#d1d5db]"}`}>
                          {isSelected && <img src={`${ICONS}/check.svg`} alt="" width="12" height="12" />}
                        </span>
                        <span className="flex-1 min-w-0 text-[#1a1917] text-[14px] font-medium leading-5">{brand}</span>
                      </button>
                    );
                  })}
                </div>
                {brands.length > BRANDS_SHOWN && (
                  <button
                    type="button"
                    onClick={() => setAllBrands(a => !a)}
                    className="bg-[rgba(238,229,229,0.36)] flex items-center justify-between w-full px-4 py-1 rounded-b-[10px]"
                  >
                    <span className="text-[#2563eb] text-[14px] font-semibold leading-5">{allBrands ? "View Less" : "View More"}</span>
                    <img src={`${ICONS}/chevron-down-blue.svg`} alt="" width="16" height="16" className={`transition-transform ${allBrands ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 p-4">
          <div className="flex items-center justify-between">
            <SectionTitle>Seating Capacity</SectionTitle>
            <span className="text-[#181818] text-[14px] tracking-[0.28px]">Seats</span>
          </div>
          <div className="flex items-center gap-2">
            {[4, 5, 6, 7].map(seat => {
              const isSelected = filters["Seater"]?.includes(String(seat));
              return (
                <button
                  key={seat}
                  type="button"
                  onClick={() => toggle("Seater", String(seat))}
                  className={`flex-1 min-w-0 h-8 rounded-[20px] text-[13px] ${isSelected ? "bg-[rgba(184,177,251,0.32)] border-[1.5px] border-[#babdee] text-[#7f1d1d] font-bold" : "bg-white border border-[#e2e8f0] text-[#475569] font-semibold"}`}
                >
                  {seat}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer — fixed below the scrolling sections */}
      <div className="shrink-0 border-t border-[#e5e7eb] flex flex-col gap-3 px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between whitespace-nowrap">
          <span className="text-[#9ca3af] text-[13px] font-medium">{count} {count === 1 ? "Car" : "Cars"} Found</span>
          <button type="button" onClick={resetAll} className="text-[#6262f2] text-[14px] font-semibold">Reset All</button>
        </div>
        {/* Side by side when both fit, stacked at full width on the narrowest phones */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => onApply({ filters, sort, yearFrom: from, yearTo: to })}
            className="flex-[1000_1_110px] h-[52px] rounded-[8px] border border-[#6595ff] text-white text-[16px] font-semibold drop-shadow-[0px_10px_12px_rgba(26,86,219,0.2)]"
            style={{ backgroundImage: APPLY_GRADIENT }}
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-[1_0_108px] h-[52px] px-2 rounded-[8px] bg-[#f5f5f5] border border-[#eee] flex items-center justify-center text-[#002b5b] text-[14px] font-bold"
          >
            <img src={`${ICONS}/x.svg`} alt="" width="24" height="24" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterSheetMob;
